import type { PodBrand } from '@/types/pod'

/**
 * The Cornwall Play Safety pod — branding layer (browser side).
 *
 * Cornwall Play Safety Ltd (company 17399978, Michael Stevenson) near St
 * Austell, carrying out outdoor play area inspections, playground equipment
 * repairs and safety surfacing across Cornwall.
 *
 * The corpus and system prompt deliberately state NO legal form. They said
 * "sole trader", which was wrong — this is a limited company — and the fix was
 * to remove the claim rather than to restate it: the assistant has no reason to
 * describe Michael's company's legal form to a customer, and putting new wording
 * about someone else's business into an owner-approved corpus is not ours to do.
 * If he wants "Ltd" said explicitly, that is his call and a one-line addition.
 * The billing path is unaffected — a Ltd is a CUSTOMER, not an operator pod.
 *
 * SOURCE OF TRUTH. Every value below is lifted from the cornwallplaysafety.co.uk
 * source (all fifteen pages, read 07 Aug 2026). Nothing is invented — the same
 * discipline the grounding fence enforces on the corpus applies to the config.
 *
 * PALETTE — SAMPLED, NOT GUESSED. Unlike the ReZabond pod, this site publishes
 * its palette explicitly in src/styles/global.css as named custom properties.
 * The values below are those exact hex values converted to the RGB triplets the
 * Tailwind config expects: green #12483A (trust/safety), gorse #F0A202 (CTA
 * only, hence accent), ink #1C2321, sand #F5F4F0, line #DEDCD4, and the site's
 * own high-risk red #B3382C for danger. The assistant therefore renders in the
 * site's real colours rather than an approximation of them.
 *
 * CONTACT. Phone and email are the business's PUBLIC contact routes as published
 * on the contact page. They are the same routes a visitor would find anyway.
 * The SMS notify target belongs in workers/pod.ts, never here.
 *
 * ASSISTANT NAME. "Piran" — after St Piran, Cornwall's patron saint and the
 * county flag. Short, unmistakably Cornish, and distinct from Bell, Rafferty,
 * Rezzi and Reva.
 */
export const playsafetyPod: PodBrand = {
  id: 'playsafety',
  brandName: 'Cornwall Play Safety',
  productName: 'Cornwall Play Safety',
  assistantName: 'Piran',
  tagline: 'Outdoor play area inspections, equipment repairs and safety surfacing across Cornwall',
  roleLabel: 'enquiry assistant',
  welcomeIntro:
    "Ask me about inspections, equipment repairs, safety surfacing or the areas we cover. Anything about your own site specifically needs Michael to look at it, so I'll take your number and get him to call you back.",
  starters: [
    'What areas of Cornwall do you cover?',
    'Do you carry out annual inspections?',
    'Can you quote against another inspector’s report?',
    'How do I get a quote?',
  ],
  inputPrompt: 'Ask Piran a question',
  footerNote:
    "Piran answers from Cornwall Play Safety's own information — anything site-specific goes straight to Michael.",
  theme: {
    // Sampled from src/styles/global.css on cornwallplaysafety.co.uk.
    '--color-brand-primary': '18 72 58', // green #12483A
    '--color-brand-primary-hover': '12 50 39', // green-dk #0C3227
    '--color-brand-accent': '240 162 2', // gorse #F0A202
    '--color-surface-base': '245 244 240', // sand #F5F4F0
    '--color-surface-raised': '255 255 255', // paper #FFFFFF
    '--color-surface-subtle': '234 232 225', // sand-2 #EAE8E1
    '--color-text-primary': '28 35 33', // ink #1C2321
    '--color-text-muted': '85 96 92', // ink-soft #55605C
    '--color-text-danger': '179 56 44', // risk-high #B3382C
    '--color-border-subtle': '222 220 212', // line #DEDCD4
  },
  // Opt in to the "Built by Foredesk" credit under the composer. This is
  // Michael's own business and Michael's own decision, so the mark is welcome
  // here; it is NOT switched on for pods belonging to other people without
  // asking them first.
  builtByForedesk: true,
  // DELIBERATELY EMPTY. The engine header renders contact.email and
  // contact.phone when present; both are omitted here so the assistant's header
  // shows only "Piran · Cornwall Play Safety · enquiry assistant".
  //
  // WHY. This pod is EMBEDDED in cornwallplaysafety.co.uk, and that page already
  // carries the phone number in the utility bar, the sticky header, the line
  // directly beneath the frame, and the footer. Repeating it a fifth time inside
  // the frame added visual noise to a small box without adding a route. Piran
  // still gives the number in conversation where it is genuinely the answer, and
  // the SMS/callback path is unaffected — this is presentation only.
  //
  // A pod that is NOT embedded (a standalone chat surface on its own URL) should
  // populate these, because there is no surrounding page to carry them.
  contact: {},
}
